import React, { useState, useEffect } from "react";
import { 
  Heart, 
  Activity, 
  Thermometer, 
  Clock, 
  ChevronDown
} from "lucide-react";
import { Link } from "react-router-dom";
import PatientSidebar from "../../components/PatientSidebar";
import "./VitalsHistory.css";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import { ref, onValue, query, limitToLast, set, push } from "firebase/database";

const VitalsHistory = () => {
  const { currentUser } = useAuth();
  const [timeRange, setTimeRange] = useState("24h");
  const [currentVitals, setCurrentVitals] = useState({ hr: 0, spo2: 0, temp: 0 });
  const [historyData, setHistoryData] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    // 1. Current Live Registry Listener
    const liveRef = ref(db, `vitals/${currentUser.uid}`);
    const unsubscribeLive = onValue(liveRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCurrentVitals({
          hr: data.heartRate || 0,
          spo2: data.spo2 || 0,
          temp: data.temperature || 0
        });
      }
    });

    // 2. Historical Temporal Registry Listener
    const historyRef = ref(db, `vitals_history/${currentUser.uid}`);
    const latestQuery = query(historyRef, limitToLast(25));
    const unsubscribeHistory = onValue(latestQuery, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const sorted = Object.values(data).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        setHistoryData(sorted);
      }
    });

    // 3. Simulation Relay: IoT Sensor Simulation (DEMO_MODE)
    // Ensures real-time data flow persists while auditing the history node
    const interval = setInterval(() => {
      const newHr = 72 + Math.floor(Math.random() * 20);
      const newSpo2 = 96 + Math.floor(Math.random() * 4);
      const newTemp = 36.5 + Math.random() * 1.2;
      
      const updateData = {
        heartRate: newHr,
        spo2: newSpo2,
        temperature: newTemp,
        bloodPressure: `${110 + Math.floor(Math.random() * 20)}/${70 + Math.floor(Math.random() * 15)}`,
        lastSync: new Date().toISOString()
      };

      // Push to Live Registry
      set(liveRef, updateData);
      
      // Push to Temporal Registry
      push(historyRef, {
        hr: newHr,
        spo2: newSpo2,
        temp: newTemp,
        timestamp: new Date().toISOString()
      });
    }, 5000);

    return () => {
      unsubscribeLive();
      unsubscribeHistory();
      clearInterval(interval);
    };
  }, [currentUser]);

  const hrPoints = historyData.length > 0 ? historyData.map(d => d.hr) : [0];
  const spo2Points = historyData.length > 0 ? historyData.map(d => d.spo2) : [0];
  const tempPoints = historyData.length > 0 ? historyData.map(d => d.temp) : [0];

  const avgHR = historyData.length > 0 ? (historyData.reduce((acc, curr) => acc + (curr.hr || 0), 0) / historyData.length).toFixed(0) : 0;
  const avgSpO2 = historyData.length > 0 ? (historyData.reduce((acc, curr) => acc + (curr.spo2 || 0), 0) / historyData.length).toFixed(0) : 0;
  const avgTemp = historyData.length > 0 ? (historyData.reduce((acc, curr) => acc + (curr.temp || 0), 0) / historyData.length).toFixed(1) : 0;
  const alertCount = historyData.filter(d => (d.hr > 90 || d.temp > 37.5)).length;

  const getTimeLabels = () => {
    if (historyData.length === 0) return ["--:--", "--:--", "--:--"];
    const indices = [0, Math.floor(historyData.length / 2), historyData.length - 1];
    return indices.map(idx => {
      const d = historyData[idx];
      if (!d || !d.timestamp) return "--:--";
      return new Date(d.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    });
  };

  const timeLabels = getTimeLabels();

  const renderTrend = (points, color, min, max) => {
    if (historyData.length === 0) {
      return (
        <div className="empty-chart-state">
          <Activity size={40} className="icon-dim pulse" />
          <p>AWAITING_TELEMETRY_SYNC...</p>
        </div>
      );
    }
    
    const width = 800;
    const height = 180;
    const padding = 20;
    
    const scaleY = (val) => height - padding - ((val - min) / (max - min)) * (height - 2 * padding);
    const scaleX = (idx) => padding + (idx * (width - 2 * padding)) / (points.length - 1);

    const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${scaleY(p)}`).join(' ');

    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="trend-svg">
        <defs>
          <linearGradient id={`grad-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.2 }} />
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        
        {[0, 1, 2, 3, 4].map(i => {
          const y = padding + (i * (height - 2 * padding)) / 4;
          return <line key={i} x1={padding} y1={y} x2={width - padding} y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />;
        })}

        <path d={`${pathData} L ${width - padding} ${height - padding} L ${padding} ${height - padding} Z`} fill={`url(#grad-${color})`} />
        <path d={pathData} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        
        {points.map((p, i) => (
          <circle key={i} cx={scaleX(i)} cy={scaleY(p)} r="3" fill={color} className="data-point" />
        ))}
      </svg>
    );
  };

  return (
    <div className="dashboard-shell">
      <PatientSidebar />

      <main className="dashboard-content">
        <header className="history-header">
          <div className="header-text">
            <h1>Vitals History</h1>
            <p>Review your real-time health data trends</p>
          </div>

          <div className="live-status-ticker glass-card">
            <div className="ticker-item">
              <span className="ticker-dot pulse"></span>
              <span className="ticker-label">LIVE_TELEMETRY</span>
            </div>
            <div className="ticker-item">
              <Heart size={14} className="ticker-icon-hr" />
              <span className="ticker-val">{currentVitals.hr}</span>
            </div>
            <div className="ticker-item">
              <Activity size={14} className="ticker-icon-spo2" />
              <span className="ticker-val">{currentVitals.spo2}%</span>
            </div>
            <div className="ticker-item">
              <Thermometer size={14} className="ticker-icon-temp" />
              <span className="ticker-val">{currentVitals.temp.toFixed(1)}°C</span>
            </div>
          </div>

          <div className="time-tabs">
            {["24h", "7d", "30d"].map(range => (
              <button 
                key={range}
                className={`tab-btn ${timeRange === range ? "active" : ""}`}
                onClick={() => setTimeRange(range)}
              >
                {range === "24h" ? "Last 24 Hours" : range === "7d" ? "Last 7 Days" : "Last 30 Days"}
              </button>
            ))}
          </div>
        </header>

        <div className="scrollable-content history-grid">
          {/* Heart Rate Chart */}
          <div className="history-chart-card glass-card">
            <div className="chart-header">
              <h3>Heart Rate ({timeRange})</h3>
            </div>
            <div className="chart-body">
              {renderTrend(hrPoints, "#2563eb", 40, 140)}
            </div>
            <div className="chart-footer">
              <div className="time-labels">
                {timeLabels.map((lbl, i) => <span key={i}>{lbl}</span>)}
              </div>
            </div>
          </div>

          {/* SpO2 Chart */}
          <div className="history-chart-card glass-card">
            <div className="chart-header">
              <h3>SpO2 ({timeRange})</h3>
            </div>
            <div className="chart-body">
              {renderTrend(spo2Points, "#2563eb", 80, 100)}
            </div>
            <div className="chart-footer">
              <div className="time-labels">
                {timeLabels.map((lbl, i) => <span key={i}>{lbl}</span>)}
              </div>
            </div>
          </div>

          {/* Temperature Chart */}
          <div className="history-chart-card glass-card">
            <div className="chart-header">
              <h3>Temperature ({timeRange})</h3>
            </div>
            <div className="chart-body">
              {renderTrend(tempPoints, "#f59e0b", 34, 42)}
            </div>
            <div className="chart-footer">
              <div className="time-labels">
                {timeLabels.map((lbl, i) => <span key={i}>{lbl}</span>)}
              </div>
            </div>
          </div>

          {/* Clinical Analysis HUD */}
          <div className="summary-section">
            <div className="summary-card glass-card">
              <div className="summary-header">
                <div>
                  <h3>Clinical Analysis Node</h3>
                  <p className="summary-subtitle">Telemetry aggregation for {timeRange} window</p>
                </div>
                <div className={`system-status ${alertCount > 0 ? "status-warning" : "status-stable"}`}>
                  <span className="status-dot"></span>
                  <span>{alertCount > 0 ? "SYSTEM_CRITICAL" : "SYSTEM_STABLE"}</span>
                </div>
              </div>

              <div className="stats-grid">
                <div className="stat-module">
                  <div className="module-header">
                    <Heart size={14} className="ticker-icon-hr" />
                    <span>AVG_HEART_RATE</span>
                  </div>
                  <div className="module-body">
                    <span className="module-val">{avgHR}</span>
                    <span className="module-unit">BPM</span>
                  </div>
                </div>

                <div className="stat-module">
                  <div className="module-header">
                    <Activity size={14} className="ticker-icon-spo2" />
                    <span>AVG_OXYGEN</span>
                  </div>
                  <div className="module-body">
                    <span className="module-val">{avgSpO2}</span>
                    <span className="module-unit">%</span>
                  </div>
                </div>

                <div className="stat-module">
                  <div className="module-header">
                    <Thermometer size={14} className="ticker-icon-temp" />
                    <span>AVG_THERMAL</span>
                  </div>
                  <div className="module-body">
                    <span className="module-val">{avgTemp}</span>
                    <span className="module-unit">°C</span>
                  </div>
                </div>

                <div className="stat-module alert-module">
                  <div className="module-header">
                    <Activity size={14} />
                    <span>ALERT_REGISTRY</span>
                  </div>
                  <div className="module-body">
                    <span className={`module-val ${alertCount > 0 ? "text-alert" : ""}`}>{alertCount}</span>
                    <span className="module-unit">EVENTS</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VitalsHistory;
